import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient } from "@/App";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TodayBriefing } from "@/components/dashboard/TodayBriefing";
import { 
  FolderKanban, 
  MapPin,
  Clock,
  ArrowRight,
  Video,
  CheckSquare,
  Sparkles
} from "lucide-react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const statVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15
    }
  }
};

// Architecture quotes - changes daily
const architectureQuotes = [
  { text: "Architecture is the learned game, correct and magnificent, of forms assembled in the light.", author: "Le Corbusier" },
  { text: "Less is more.", author: "Ludwig Mies van der Rohe" },
  { text: "God is in the details.", author: "Ludwig Mies van der Rohe" },
  { text: "Form follows function.", author: "Louis Sullivan" },
  { text: "Architecture is frozen music.", author: "Johann Wolfgang von Goethe" },
  { text: "The mother art is architecture.", author: "Frank Lloyd Wright" },
  { text: "Space is the breath of art.", author: "Frank Lloyd Wright" },
  { text: "Every great architect is — necessarily — a great poet.", author: "Frank Lloyd Wright" },
  { text: "Design is not just what it looks like. Design is how it works.", author: "Steve Jobs" },
  { text: "Simplicity is the ultimate sophistication.", author: "Leonardo da Vinci" },
  { text: "Architecture should speak of its time and place.", author: "Frank Gehry" },
  { text: "Light creates ambience and feel of a place.", author: "Le Corbusier" },
  { text: "To create, one must first question everything.", author: "Eileen Gray" },
  { text: "A room is not a room without natural light.", author: "Louis Kahn" },
  { text: "The details are not the details. They make the design.", author: "Charles Eames" }
];

const getDailyQuote = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return architectureQuotes[dayOfYear % architectureQuotes.length];
};

// Elegant Analog Clock Component
function ElegantClock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const seconds = time.getSeconds();
  const minutes = time.getMinutes();
  const hours = time.getHours() % 12;
  
  const secondAngle = (seconds / 60) * 360;
  const minuteAngle = ((minutes + seconds / 60) / 60) * 360;
  const hourAngle = ((hours + minutes / 60) / 12) * 360;

  return (
    <div className="relative w-32 h-32 sm:w-40 sm:h-40">
      {/* Outer glow */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF4500]/20 to-transparent blur-xl animate-pulse-slow" />
      
      {/* Clock face */}
      <div className="absolute inset-0 rounded-full border border-[#262626] bg-[#0A0A0A]/90 backdrop-blur-sm">
        {/* Hour markers */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              width: i % 3 === 0 ? '2px' : '1px',
              height: i % 3 === 0 ? '8px' : '5px',
              backgroundColor: i % 3 === 0 ? '#FF4500' : '#444',
              transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-55px)`,
            }}
          />
        ))}
        
        {/* Center dot */}
        <div className="absolute left-1/2 top-1/2 w-2 h-2 bg-[#FF4500] rounded-full transform -translate-x-1/2 -translate-y-1/2 z-20 shadow-lg shadow-[#FF4500]/50" />
        
        {/* Hour hand */}
        <div
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            transform: `translateX(-50%) translateY(-100%) rotate(${hourAngle}deg)`,
            width: '3px',
            height: '25%',
            background: 'linear-gradient(to top, #FFFFFF, #888)',
            borderRadius: '2px',
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        
        {/* Minute hand */}
        <div
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            transform: `translateX(-50%) translateY(-100%) rotate(${minuteAngle}deg)`,
            width: '2px',
            height: '35%',
            background: 'linear-gradient(to top, #CCCCCC, #666)',
            borderRadius: '1px',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        
        {/* Second hand */}
        <div
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            transform: `translateX(-50%) translateY(-100%) rotate(${secondAngle}deg)`,
            width: '1px',
            height: '40%',
            backgroundColor: '#FF4500',
            boxShadow: '0 0 10px #FF4500',
          }}
        />
      </div>
    </div>
  );
}

// Animated greeting text
function AnimatedGreeting({ name }) {
  const [displayText, setDisplayText] = useState("");
  const fullText = `Hello, ${name}`;
  
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, [fullText]);

  return (
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">
      <span className="inline-block">{displayText}</span>
      <span className="inline-block w-[2px] h-8 sm:h-10 bg-[#FF4500] ml-1 animate-blink" />
    </h1>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quote] = useState(getDailyQuote());
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [fetchStats]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const userName = user?.name || 'Ar. Jerin';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 animate-pulse">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="page-content" 
      data-testid="dashboard"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Hero Section with Greeting */}
      <motion.div 
        variants={itemVariants}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0D0D0D] via-[#141414] to-[#0D0D0D] border border-[#1A1A1A] p-4 sm:p-10 mb-6 sm:mb-8"
      >
        {/* Decorative elements */}
        <motion.div 
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#FF4500]/5 to-transparent rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#FF4500]/3 to-transparent rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        
        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-4 sm:gap-8">
          {/* Left: Greeting & Quote */}
          <div className="flex-1 text-center lg:text-left w-full">
            {/* Date Badge - Always visible at top */}
            <div className="flex justify-center lg:justify-start mb-3 sm:mb-4 animate-slide-in-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF4500]/10 rounded-full text-[#FF4500] text-[10px] sm:text-xs">
                <Sparkles className="w-3 h-3" />
                <span className="font-medium">{formatDate(currentTime)}</span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6 animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
              <AnimatedGreeting name={userName} />
            </div>
            
            {/* Quote - Hidden on very small screens */}
            <div className="hidden sm:block max-w-lg mx-auto lg:mx-0 animate-slide-in-left" style={{ animationDelay: '0.3s' }}>
              <div className="relative pl-4 border-l-2 border-[#FF4500]/30">
                <p className="text-sm sm:text-base text-gray-400 italic leading-relaxed">
                  "{quote.text}"
                </p>
                <p className="text-xs text-[#FF4500] mt-2 tracking-widest uppercase">
                  — {quote.author}
                </p>
              </div>
            </div>
          </div>
          
          {/* Right: Clock */}
          <div className="flex flex-col items-center animate-slide-in-right">
            <ElegantClock />
            <p className="text-xl sm:text-3xl font-light text-white mt-3 sm:mt-4 tracking-wider">
              {formatTime(currentTime)}
            </p>
          </div>
        </div>
        
        {/* Mobile Quote - Below clock */}
        <div className="sm:hidden mt-4 pt-4 border-t border-white/5" style={{ animationDelay: '0.3s' }}>
          <p className="text-xs text-gray-500 italic text-center leading-relaxed">
            "{quote.text}"
          </p>
          <p className="text-[10px] text-[#FF4500]/60 mt-1 tracking-widest uppercase text-center">
            — {quote.author}
          </p>
        </div>
      </motion.div>

      <TodayBriefing />

      {/* Stats Summary Row */}
      <motion.div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8"
        variants={containerVariants}
      >
        {/* Projects */}
        <motion.div variants={statVariants}>
          <Link to="/projects" className="group block">
            <motion.div 
              className="relative overflow-hidden rounded-xl bg-[#141414] border border-[#1A1A1A] p-4 sm:p-5 hover:border-[#FF4500]/30 transition-colors duration-300"
              whileHover={{ y: -4, boxShadow: "0 20px 40px -15px rgba(255,69,0,0.2)" }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF4500]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <FolderKanban className="w-5 h-5 text-[#FF4500]" />
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#FF4500] group-hover:translate-x-1 transition-all" />
                </div>
                <motion.p 
                  className="text-2xl sm:text-3xl font-bold text-white mb-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                >
                  {stats?.active_projects || 0}
                </motion.p>
                <p className="text-xs text-gray-500">Active Projects</p>
                <p className="text-[10px] text-gray-600 mt-1">{stats?.total_projects || 0} total</p>
              </div>
            </motion.div>
          </Link>
        </motion.div>

        {/* Today's Tasks */}
        <motion.div variants={statVariants}>
          <Link to="/tasks" className="group block">
            <motion.div 
              className="relative overflow-hidden rounded-xl bg-[#141414] border border-[#1A1A1A] p-4 sm:p-5 hover:border-[#10B981]/30 transition-colors duration-300"
              whileHover={{ y: -4, boxShadow: "0 20px 40px -15px rgba(16,185,129,0.2)" }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <CheckSquare className="w-5 h-5 text-[#10B981]" />
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#10B981] group-hover:translate-x-1 transition-all" />
                </div>
                <motion.p 
                  className="text-2xl sm:text-3xl font-bold text-white mb-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
                >
                  {stats?.pending_tasks || 0}
                </motion.p>
                <p className="text-xs text-gray-500">Pending Tasks</p>
                {stats?.overdue_tasks > 0 && (
                  <motion.p 
                    className="text-[10px] text-red-400 mt-1"
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {stats.overdue_tasks} overdue
                  </motion.p>
                )}
              </div>
            </motion.div>
          </Link>
        </motion.div>

        {/* Today's Meetings */}
        <motion.div variants={statVariants}>
          <Link to="/meetings" className="group block">
            <motion.div 
              className="relative overflow-hidden rounded-xl bg-[#141414] border border-[#1A1A1A] p-4 sm:p-5 hover:border-[#3B82F6]/30 transition-colors duration-300"
              whileHover={{ y: -4, boxShadow: "0 20px 40px -15px rgba(59,130,246,0.2)" }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <Video className="w-5 h-5 text-[#3B82F6]" />
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#3B82F6] group-hover:translate-x-1 transition-all" />
                </div>
                <motion.p 
                  className="text-2xl sm:text-3xl font-bold text-white mb-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.5 }}
                >
                  {stats?.upcoming_meetings?.length || 0}
                </motion.p>
                <p className="text-xs text-gray-500">Upcoming Meetings</p>
              </div>
            </motion.div>
          </Link>
        </motion.div>

        {/* Site Visits */}
        <motion.div variants={statVariants}>
          <Link to="/site-visits" className="group block">
            <motion.div 
              className="relative overflow-hidden rounded-xl bg-[#141414] border border-[#1A1A1A] p-4 sm:p-5 hover:border-[#F59E0B]/30 transition-colors duration-300"
              whileHover={{ y: -4, boxShadow: "0 20px 40px -15px rgba(245,158,11,0.2)" }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <MapPin className="w-5 h-5 text-[#F59E0B]" />
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#F59E0B] group-hover:translate-x-1 transition-all" />
                </div>
                <motion.p 
                  className="text-2xl sm:text-3xl font-bold text-white mb-1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.6 }}
                >
                  {stats?.upcoming_visits?.length || 0}
                </motion.p>
                <p className="text-xs text-gray-500">Site Visits</p>
              </div>
            </motion.div>
          </Link>
        </motion.div>
      </motion.div>

      {/* Main Content Grid */}
      <motion.div 
        className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6"
        variants={containerVariants}
      >
        
        {/* To-Do Brief */}
        <div className="lg:col-span-1 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <div className="rounded-xl bg-[#141414] border border-[#1A1A1A] overflow-hidden h-full">
            <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-[#10B981]" />
                <h3 className="text-sm font-medium text-white">Today's Tasks</h3>
              </div>
              <Link to="/tasks" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto">
              {stats?.today_tasks?.length > 0 ? (
                stats.today_tasks.slice(0, 5).map((task, i) => (
                  <div 
                    key={task.task_id || i}
                    className="group flex items-start gap-3 p-3 rounded-lg bg-[#0D0D0D] hover:bg-[#1A1A1A] transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      task.priority === 'high' ? 'bg-red-500' : 
                      task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{task.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{task.due_date}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CheckSquare className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No tasks for today</p>
                  <p className="text-[10px] text-gray-600 mt-1">Enjoy your free time!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Meetings Brief */}
        <div className="lg:col-span-1 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className="rounded-xl bg-[#141414] border border-[#1A1A1A] overflow-hidden h-full">
            <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-[#3B82F6]" />
                <h3 className="text-sm font-medium text-white">Upcoming Meetings</h3>
              </div>
              <Link to="/meetings" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto">
              {stats?.upcoming_meetings?.length > 0 ? (
                stats.upcoming_meetings.slice(0, 4).map((meeting, i) => (
                  <div 
                    key={meeting.meeting_id || i}
                    className="group flex items-center gap-3 p-3 rounded-lg bg-[#0D0D0D] hover:bg-[#1A1A1A] transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      meeting.meeting_type === 'online' ? 'bg-[#3B82F6]/20' : 'bg-[#10B981]/20'
                    }`}>
                      {meeting.meeting_type === 'online' ? 
                        <Video className="w-4 h-4 text-[#3B82F6]" /> : 
                        <MapPin className="w-4 h-4 text-[#10B981]" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{meeting.project_name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{meeting.agenda}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-white">{meeting.scheduled_date}</p>
                      <p className="text-[10px] text-gray-500">{meeting.scheduled_time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Video className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No upcoming meetings</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Site Visits Brief */}
        <motion.div variants={cardVariants} className="lg:col-span-1">
          <div className="rounded-xl bg-[#141414] border border-[#1A1A1A] overflow-hidden h-full">
            <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F59E0B]" />
                <h3 className="text-sm font-medium text-white">Site Visits</h3>
              </div>
              <Link to="/site-visits" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto">
              {stats?.upcoming_visits?.length > 0 ? (
                stats.upcoming_visits.slice(0, 4).map((visit, i) => (
                  <motion.div 
                    key={visit.visit_id || i}
                    className="group flex items-center gap-3 p-3 rounded-lg bg-[#0D0D0D] hover:bg-[#1A1A1A] transition-colors"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ x: 4 }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-[#F59E0B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{visit.project_name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{visit.purpose}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-white">{visit.scheduled_date}</p>
                      <p className="text-[10px] text-gray-500">{visit.scheduled_time}</p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <MapPin className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No upcoming visits</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Recent Projects */}
        <motion.div variants={cardVariants} className="lg:col-span-3">
          <div className="rounded-xl bg-[#141414] border border-[#1A1A1A] overflow-hidden">
            <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderKanban className="w-4 h-4 text-[#FF4500]" />
                <h3 className="text-sm font-medium text-white">Recent Projects</h3>
              </div>
              <Link to="/projects" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="p-4">
              {stats?.recent_projects?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stats.recent_projects.slice(0, 6).map((project, i) => (
                    <motion.div
                      key={project.project_id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.08 }}
                      whileHover={{ y: -2 }}
                    >
                      <Link 
                        to={`/projects/${project.project_id}`}
                        className="block p-4 rounded-lg bg-[#0D0D0D] border border-[#1A1A1A] hover:border-[#FF4500]/30 transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium text-white truncate flex-1 group-hover:text-[#FF4500] transition-colors">
                            {project.name}
                          </h4>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                            project.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' :
                            project.status === 'completed' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' :
                            'bg-[#F59E0B]/20 text-[#F59E0B]'
                          }`}>
                            {project.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{project.client_name}</p>
                        <div className="mt-3 flex items-center justify-between text-[10px] text-gray-600">
                          <span>{project.project_type}</span>
                          <span>{project.progress || 0}%</span>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-2 h-1 bg-[#262626] rounded-full overflow-hidden">
                          <motion.div 
                            className="h-full bg-gradient-to-r from-[#FF4500] to-[#FF6B35]"
                            initial={{ width: 0 }}
                            animate={{ width: `${project.progress || 0}%` }}
                            transition={{ duration: 1, delay: i * 0.1, ease: "easeOut" }}
                          />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FolderKanban className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No projects yet</p>
                  <Link to="/projects" className="inline-block mt-3 text-xs text-[#FF4500] hover:underline">
                    Create your first project
                  </Link>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
